import React from 'react';
import { Form, FormControl } from 'react-bootstrap';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from 'react-bootstrap/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS,
  logStockItemDatePickerOpen,
} from './stockItemDatePicker';
import StoragePlaceSelect from './StoragePlaceSelect';

/**
 * Represents a component for manual stock item creation.
 *
 * This component provides a form interface for creating stock items with fields for:
 * - Opened By (text input)
 * - Batch Number (text input)
 * - Expiry Date (date picker)
 * - In Current Use (checkbox)
 * - Opened On (date picker)
 *
 * Note: Date fields use setFieldTouched instead of handleBlur because react-datepicker
 * doesn't provide standard form events with name properties, which would cause
 * Formik to lose track of field values on blur. Calendars use
 * STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS so they are not clipped by the modal body.
 *
 * @param {Object} props - The component props.
 * @param {number} props.stockItemIndex - The index of the stock item in the items array.
 * @param {Object} props.formik - The Formik instance containing values, errors, touched state, and form methods.
 * @param {Function} props.onItemChange - The callback function for handling stock item changes (index, field, value).
 * @param {Function} props.onItemDelete - The callback function for deleting a stock item (event, index).
 * @param {boolean} props.showDeleteButton - Whether to show the delete button (default: true).
 * @param {Array|null} [props.storageNodes] - Lab storage tree. When set, the lot can be placed on a shelf, drawer, rack, or box.
 * @returns {JSX.Element} The ManualStockItemCreationComponent.
 */
const ManualStockItemCreationComponent = ({
  stockItemIndex,
  formik,
  onItemChange,
  onItemDelete,
  showDeleteButton = true,
  storageNodes = null,
}) => {
  // Field names for Formik form fields
  // Note: Using bracket notation for array indices in Formik field paths
  const openedByFieldName = `items[${stockItemIndex}].openedBy`;
  const batchFieldName = `items[${stockItemIndex}].batch`;
  const expiryFieldName = `items[${stockItemIndex}].expiry`;
  const inUseFieldName = `items[${stockItemIndex}].inUse`;
  const openedOnFieldName = `items[${stockItemIndex}].openedOn`;

  // Function used to update the relevant stock item instantly
  const handleStockItemInstantChange = (name, value) => {
    onItemChange(stockItemIndex, name, value);
  };
  return (
    <div
      className={`${
        stockItemIndex > 0 && 'mt-3'
      } border border-secondary-subtle rounded p-3`}
      style={{ backgroundColor: '#efef8f' }}
    >
      <Form.Group
        controlId={`itemOpenedBy${stockItemIndex}`}
        className="field-margin"
      >
        <Form.Label>Opened By</Form.Label>
        <FormControl
          name={openedByFieldName}
          type="text"
          value={formik?.values?.items?.[stockItemIndex]?.openedBy || ''}
          onChange={event => {
            formik.handleChange(event);
            const { value } = event.target;
            handleStockItemInstantChange('opened_by_name', value);
          }}
          onBlur={formik.handleBlur}
          onFocus={() => formik.setFieldTouched(openedByFieldName, true)}
          isValid={
            formik?.touched?.items?.[stockItemIndex]?.openedBy &&
            !formik?.errors?.items?.[stockItemIndex]?.openedBy &&
            formik.values?.items?.[stockItemIndex]?.openedBy
          }
          isInvalid={
            formik?.touched?.items?.[stockItemIndex]?.openedBy &&
            !!formik?.errors?.items?.[stockItemIndex]?.openedBy
          }
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items &&
            formik?.errors?.items?.length > stockItemIndex &&
            formik?.errors?.items?.[stockItemIndex]?.openedBy}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group
        controlId={`itemBatch${stockItemIndex}`}
        className="field-margin"
      >
        <Form.Label>Batch Number</Form.Label>
        <FormControl
          name={batchFieldName}
          type="text"
          value={formik?.values?.items?.[stockItemIndex]?.batch || ''}
          onChange={event => {
            formik.handleChange(event);
            const { value } = event.target;
            handleStockItemInstantChange('batch', value);
          }}
          onBlur={formik.handleBlur}
          onFocus={() => formik.setFieldTouched(batchFieldName, true)}
          isValid={
            formik?.touched?.items?.[stockItemIndex]?.batch &&
            !formik?.errors?.items?.[stockItemIndex]?.batch &&
            formik.values?.items?.[stockItemIndex]?.batch
          }
          isInvalid={
            formik?.touched?.items?.[stockItemIndex]?.batch &&
            !!formik?.errors?.items?.[stockItemIndex]?.batch
          }
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items &&
            formik?.errors?.items?.length > stockItemIndex &&
            formik?.errors?.items?.[stockItemIndex]?.batch}
        </Form.Control.Feedback>
      </Form.Group>

      {/* Expiry date field using DatePicker component */}
      <Form.Group
        controlId={`expiry${stockItemIndex}`}
        className="field-margin"
      >
        <Form.Label>Expiry Date</Form.Label>
        <DatePicker
          {...STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS}
          selected={
            formik?.values?.items?.[stockItemIndex]?.expiry
              ? new Date(formik?.values?.items?.[stockItemIndex]?.expiry)
              : null
          }
          onCalendarOpen={() =>
            logStockItemDatePickerOpen('expiry', { stockItemIndex })
          }
          onChange={date => {
            if (date) {
              // Format date as YYYY-MM-DD for consistent storage
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const dateValue = `${year}-${month}-${day}`;

              formik.setFieldValue(expiryFieldName, dateValue);
              handleStockItemInstantChange('expiry', dateValue);
            } else {
              formik.setFieldValue(expiryFieldName, '');
              handleStockItemInstantChange('expiry', '');
            }
          }}
          dateFormat="dd/MM/yyyy"
          placeholderText="dd/mm/yyyy"
          className={`form-control ${
            formik?.touched?.items?.[stockItemIndex]?.expiry &&
            !!formik?.errors?.items?.[stockItemIndex]?.expiry
              ? 'is-invalid'
              : formik?.touched?.items?.[stockItemIndex]?.expiry &&
                !formik?.errors?.items?.[stockItemIndex]?.expiry &&
                formik?.values?.items?.[stockItemIndex]?.expiry
              ? 'is-valid'
              : ''
          }`}
          wrapperClassName="w-100"
          inputClassName="text-center"
          onFocus={() => formik.setFieldTouched(expiryFieldName, true)}
          // Use setFieldTouched instead of handleBlur because react-datepicker
          // doesn't provide a standard form event with name property
          onBlur={() => formik.setFieldTouched(expiryFieldName, true)}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          todayButton="Today"
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items &&
            formik?.errors?.items?.length > stockItemIndex &&
            formik?.errors?.items?.[stockItemIndex]?.expiry}
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group
        controlId={`formItemFulfilled${stockItemIndex}`}
        className="field-margin"
      >
        <Form.Check
          name={inUseFieldName}
          type="checkbox"
          label="In Current Use?"
          checked={formik?.values?.items?.[stockItemIndex]?.inUse}
          onChange={e => {
            const { checked } = e.target;
            formik.setFieldValue(`items.${stockItemIndex}.inUse`, checked);
            handleStockItemInstantChange('in_use', checked);
          }}
          onBlur={formik.handleBlur}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Opened On</Form.Label>
        <DatePicker
          {...STOCK_ITEM_DATEPICKER_OVERFLOW_PROPS}
          selected={
            formik?.values?.items?.[stockItemIndex]?.openedOn
              ? new Date(formik?.values?.items?.[stockItemIndex]?.openedOn)
              : null
          }
          onCalendarOpen={() =>
            logStockItemDatePickerOpen('openedOn', { stockItemIndex })
          }
          onChange={date => {
            if (date) {
              // Format date as YYYY-MM-DD for consistent storage
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const dateValue = `${year}-${month}-${day}`;

              formik.setFieldValue(openedOnFieldName, dateValue);
              handleStockItemInstantChange('opened_on', dateValue);
            } else {
              formik.setFieldValue(openedOnFieldName, '');
              handleStockItemInstantChange('opened_on', '');
            }
          }}
          dateFormat="dd/MM/yyyy"
          placeholderText="dd/mm/yyyy"
          className={`form-control ${
            formik?.touched?.items?.[stockItemIndex]?.openedOn &&
            !!formik?.errors?.items?.[stockItemIndex]?.openedOn
              ? 'is-invalid'
              : formik?.touched?.items?.[stockItemIndex]?.openedOn &&
                !formik?.errors?.items?.[stockItemIndex]?.openedOn &&
                formik?.values?.items?.[stockItemIndex]?.openedOn
              ? 'is-valid'
              : ''
          }`}
          wrapperClassName="w-100"
          inputClassName="text-center"
          onFocus={() => formik.setFieldTouched(openedOnFieldName, true)}
          // Use setFieldTouched instead of handleBlur because react-datepicker
          // doesn't provide a standard form event with name property
          onBlur={() => formik.setFieldTouched(openedOnFieldName, true)}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          todayButton="Today"
        />
        <Form.Control.Feedback type="invalid">
          {!!formik?.errors?.items &&
            formik?.errors?.items?.length > stockItemIndex &&
            formik?.errors?.items?.[stockItemIndex]?.openedOn}
        </Form.Control.Feedback>
      </Form.Group>
      {storageNodes ? (
        <Form.Group className="mb-2">
          <Form.Label>Place</Form.Label>
          <StoragePlaceSelect
            nodes={storageNodes}
            storageNodeId={formik?.values?.items?.[stockItemIndex]?.storageNodeId}
            slotIndex={formik?.values?.items?.[stockItemIndex]?.slotIndex}
            idPrefix={`manual-${stockItemIndex}`}
            onChange={({ storageNodeId, slotIndex }) => {
              onItemChange?.(stockItemIndex, 'storage_place', {
                storage_node_id: storageNodeId,
                slot_index: slotIndex,
              });
            }}
          />
        </Form.Group>
      ) : null}
      {showDeleteButton && onItemDelete && (
        <Button
          className="mt-2"
          variant="outline-danger"
          onClick={e => {
            onItemDelete(e, stockItemIndex);
          }}
        >
          <DeleteIcon />
        </Button>
      )}
    </div>
  );
};
export default ManualStockItemCreationComponent;
